import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { WebAccessControlSchedulerFormRepository } from '../repository/WebAccessControlSchedulerFormRepository';
import { WebAccessControlSchedulerFormComponent } from '../component/WebAccessControlSchedulerFormComponent';
import { WebAccessControlSchedulerFormView } from '../view/WebAccessControlSchedulerFormView';
import { templateWebAccessControlSchedulerForm } from '../view/template';
import { showErrorToast, showSuccessToast } from '../../../ui/gzToastNotification';
import { ScheduleGridEvents, ScheduleItem, SubSchedule } from '../../webAccessControlSchedulerApp/types';
import { Dependencies, SubScheduleGridEvents } from '../types';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import _ from 'lodash';
import { TimePickerEvents } from '../../../ui/gzTimePicker/types';
import { ValidationErrorCode } from '../../webAccessControlCustomPagesForm/types';
import { translateFn } from '../../../utils/translator.helper';

export class WebAccessControlSchedulerFormController extends BaseWebComponentController {
  public component: WebAccessControlSchedulerFormComponent;
  public dataRepository: WebAccessControlSchedulerFormRepository;
  public view: WebAccessControlSchedulerFormView;
  public viewConstructor: typeof WebAccessControlSchedulerFormView;
  public dataRepositoryConstructor: typeof WebAccessControlSchedulerFormRepository;
  public dependencies: Dependencies;

  constructor(component: WebAccessControlSchedulerFormComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: WebAccessControlSchedulerFormView,
      viewTemplate: templateWebAccessControlSchedulerForm,
      dataRepositoryConstructor: WebAccessControlSchedulerFormRepository,
    });
  }

  public computeEventListeners() {
    return [
      {
        element: this.view.dom.$btnCancel,
        event: 'click',
        callback: this.closeWebAccessControlSchedulerForm,
      },
      {
        element: this.view.dom.$btnSave,
        event: 'click',
        callback: this.saveWebAccessControlScheduler,
      },
      {
        element: this.view.dom.$btnUpdateScheduler,
        event: 'click',
        callback: this.updateWebAccessControlScheduler,
      },
      {
        element: this.view.dom.$btnAddNewScheduler,
        event: 'click',
        callback: this.addWebAccessControlScheduler,
      },
      {
        element: this.view.dom.$grid,
        event: SubScheduleGridEvents.CLICK_ON_DELETE_ICON,
        callback: this.onDeleteIconClick,
      },
      {
        element: this.view.dom.$grid,
        event: SubScheduleGridEvents.CLICK_ON_EDIT_ICON,
        callback: this.onEditIconClick,
      },
      {
        element: this.view.dom.$grid,
        event: SubScheduleGridEvents.CLICK_ON_UP_ICON,
        callback: this.onUpIconClick,
      },
      {
        element: this.view.dom.$grid,
        event: SubScheduleGridEvents.CLICK_ON_DOWN_ICON,
        callback: this.onDownIconClick,
      },
      {
        element: this.view.dom.$timePickerStart.Input,
        event: TimePickerEvents.CHANGE,
        callback: this.triggerSubscheduleFormValidation,
      },
      {
        element: this.view.dom.$allWebsitesFormField,
        event: SubScheduleGridEvents.CHANGE_ON_ALL_WEBSITES,
        callback: this.onAllWebsitesChange,
      }
    ];
  }

  private onAllWebsitesChange(event: CustomEvent) {
    const isChecked = event.detail.checked;
    this.view.handleAllWebsitesChange(isChecked);
  }

  private closeWebAccessControlSchedulerForm() {
    this.component.propertiesSet = false;
    this.component.isVisible = false;
    this.component.schedule = undefined;
    this.component.propertiesSet = true;
  }

  private async saveWebAccessControlScheduler() {
    this.view.dom.$btnSave.disabled = true;
    const record = this.view.getScheduleFormData();
    let error;
    if (!record?._id) {
      error = await this.createSchedule(record);
    } else {
      error = await this.updateSchedule(record);
    }

    this.view.dom.$btnSave.disabled = false;

    if (error) {
      this.displayErrorMessage(error);
      return;
    }

    const message = (!record?._id) ? translateFn('WAC Scheduler created successfully.') : translateFn('WAC Scheduler updated successfully.');
    showSuccessToast(message);

    this.closeWebAccessControlSchedulerForm();
    this.dispatchEvent(ScheduleGridEvents.SAVE_SCHEDULE);
  }

  private displayErrorMessage(error: { code: number }) {
    const inputScheduleName: GzFormFieldWebComponent = this.view.dom.$inputScheduleNameFormField;
    switch (error.code) {
      case ValidationErrorCode.DUPLICATE:
        inputScheduleName.setError(translateFn("A scheduler with the same name already exists."));
        return;
      case ValidationErrorCode.MAX_LENGTH:
        inputScheduleName.setError(translateFn('Maximum length permitted is {maxLength} characters', {maxLength: 128}));
        return;
      case ValidationErrorCode.MIN_LENGTH:
        inputScheduleName.setError(translateFn('The minimum length permitted is {minLength} characters', {minLength: 1}));
        return;
      case ValidationErrorCode.REQUIRED_ALLOW_TO_CHANGE:
        showErrorToast(translateFn("\"Allow others to change this schedule\" must be enabled to save a managed company's WAC schedule."));
        return;
      default:
        showErrorToast(translateFn("An unknown error has occurred."));
        return;
    }
  }

  private async createSchedule(record: ScheduleItem): Promise<void | string> {
    let error;
    try {
     await this.dataRepository.createRecords([record]);
    } catch (e) {
      error = e?.err || "unknownError";
    }
    return error
  }

  private async updateSchedule(record: ScheduleItem):Promise<void | string> {
    let error;
    try {
      await this.dataRepository.updateRecords([record]);
    } catch (e) {
      error = e?.err || "unknownError";
    }
    return error;
  }

  private dispatchEvent(event: ScheduleGridEvents, detail = {}) {
    this.component.dispatchEvent(
      new CustomEvent(event, {
        detail: detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private updateWebAccessControlScheduler() {
    this.handleSubScheduleSet();
    setTimeout(() => {this.view.dom.$grid.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });}, 1);
  }

  private addWebAccessControlScheduler() {
    this.view.dom.$btnAddNewScheduler.disabled = true;
    this.handleSubScheduleSet(true);
    setTimeout(() => {this.view.dom.$grid.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });}, 1);
  }

  private handleSubScheduleSet(isNewSubSchedule: boolean = false) {
    if (!this.dependencies.schedule?.subschedules) {
      return;
    }

    const subschedule = this.view.getSubscheduleFormData();
    const index = subschedule.index as number;

    if (_.isNumber(index) && !isNewSubSchedule) {
      this.dependencies.schedule.subschedules[index] = subschedule;
    } else {
      this.dependencies.schedule.subschedules.push(subschedule);
    }

    this.view.checkSubschedulesLimit();
    this.view.dom.$grid?.controller?.handleGridItemsLoad();
    this.view.resetSubScheduleFormFields();
  }

  private onDeleteIconClick(event: WebComponentEvent) {
    this.dependencies?.schedule?.subschedules.splice(event.detail.rowData.index, 1);
    this.view.checkSubschedulesLimit();
    this.view.dom.$grid?.controller?.handleGridItemsLoad();
  }

  private onEditIconClick(event: WebComponentEvent) {
    event.detail.rowData.item.index = event.detail.rowData.index;
    this.view.setSubscheduleFormData(event.detail.rowData.item);
    this.view.dom.$btnAddNewScheduler.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
  }

  private onUpIconClick(event: WebComponentEvent) {
    this.handleSubScheduleMove(event.detail.rowData.index, event.detail.rowData.index - 1);
  }

  private onDownIconClick(event: WebComponentEvent) {
    this.handleSubScheduleMove(event.detail.rowData.index, event.detail.rowData.index + 1);
  }

  private handleSubScheduleMove(currentIndex: number, toIndex: number) {
    if (!this.dependencies.schedule?.subschedules) {
      return;
    }
    const subschedule: SubSchedule = this.dependencies.schedule?.subschedules.splice(currentIndex, 1)[0];
    this.dependencies.schedule?.subschedules.splice(toIndex, 0, subschedule);
    this.view.dom.$grid?.controller?.handleGridItemsLoad();
  }

  private triggerSubscheduleFormValidation() {
    this.view.getSubscheduleFormGroup().validate();
  }
}
